import { Tooltip } from "@mui/material";
import { Icon, Tag } from "pepsico-ds";

export const TABLE_CONFIG = {
  ROWS_PER_PAGE: 10,
  NO_DATA_LABEL: "No stores available",
};

/**
 * Derives which address fields (State / District) to show based on the
 * storeManagement config from AppConfig.
 *
 * Rules:
 * - ["District"]           → district only
 * - ["State"]              → state only
 * - ["District", "State"]  → both
 * - [] / missing           → state only (default)
 */
export const getStoreAddressFields = (appConfig) => {
  const storeField = appConfig?.storeManagement?.storeField || [];
  const addressEntry = storeField.find((entry) => entry.store_address);
  const addressFields = addressEntry?.store_address || [];
  const normalized = addressFields.map((f) => f.toLowerCase());
  const hasDistrict = normalized.includes("district");
  const hasState = normalized.includes("state");
  // Default: show state when nothing is specified
  return {
    showState: hasState || !hasDistrict,
    showDistrict: hasDistrict,
  };
};

export const getStoreHeaderData = ({ showState, showDistrict } = {}) => {
  const headers = [
    { columnKey: "status", header: "Status" },
    {
      columnKey: "image",
      header: "Image",
    },
    { columnKey: "storeId", header: "Store ID" },
    { columnKey: "name", header: "Store Name" },
    { columnKey: "retailer", header: "Retailer" },
    { columnKey: "country", header: "Country" },
  ];
  if (showState !== false) {
    headers.push({ columnKey: "state", header: "State" });
  }
  if (showDistrict) {
    headers.push({ columnKey: "district", header: "District" });
  }
  headers.push(
    { columnKey: "city", header: "City" },
    { columnKey: "postcode", header: "Postal Code" },
    {
      columnKey: "latitude",
      header: "Latitude",
    },
    {
      columnKey: "longitude",
      header: "Longitude",
    },
    { columnKey: "category", header: "Store Group" },
    { columnKey: "actions", header: "Actions", className: "text-center" }
  );
  return headers;
};

export const STORE_HEADER_DATA = getStoreHeaderData({
  showState: true,
  showDistrict: false,
});

export const STORE_GROUP_HEADER_DATA = [
  {
    columnKey: "status",
    header: "Status",
  },
  {
    columnKey: "name",
    header: "Group Name",
  },
  {
    columnKey: "storeCount",
    header: "Store Count",
  },
  {
    columnKey: "actions",
    header: "Actions",
    className: "text-center",
  },
];

export const renderCategories = (categories = []) => {
  if (!categories.length) return "-";
  const [first, ...rest] = categories;
  if (!rest.length) return <span>{first}</span>;
  return (
    <Tooltip title={categories.join(", ")} arrow placement="top">
      <span style={{ cursor: "pointer" }}>
        {first} (+{rest.length})
      </span>
    </Tooltip>
  );
};

export const renderZipCodes = (zipCodes = []) => {
  if (!zipCodes.length) return "-";
  const display = zipCodes.slice(0, 2).join(", ");
  if (zipCodes.length <= 2) return <span>{display}</span>;
  return (
    <Tooltip title={zipCodes.join(", ")} arrow placement="top">
      <span style={{ cursor: "pointer" }}>{display}, ...</span>
    </Tooltip>
  );
};

export const STYLES = {
  actions: {
    display: "flex",
    gap: "12px",
    alignItems: "center",
    justifyContent: "center",
  },
  editIcon: {
    cursor: "pointer",
    color: "#007bff",
    fontSize: "16px",
  },
  deleteIcon: {
    cursor: "pointer",
    color: "#dc3545",
    fontSize: "16px",
  },
  disabledIcon: {
    cursor: "not-allowed",
    color: "#6c757d",
    fontSize: "16px",
    opacity: 0.5,
  },
  viewIcon: {
    cursor: "pointer",
    color: "#17a2b8",
    fontSize: "16px",
  },
  thumbnail: {
    width: "40px",
    height: "40px",
    objectFit: "cover",
    borderRadius: "4px",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "200px",
  },
};

export const MESSAGES = {
  LOADING: "Loading stores...",
  VIEW_STORE_TOOLTIP: "View store",
  EDIT_TOOLTIP: "Edit store",
  DELETE_TOOLTIP: "Delete store",
  DELETE_DISABLED_TOOLTIP: "Cannot delete - already deleted",
  VIEW_TOOLTIP: "View stores in group",
};

export const TAG_COLORS = {
  ACTIVE: "Green-150",
  INACTIVE: "Gray-100",
  DELETED: "Red-500",
  GROUP: "Blue-500",
};

export const VIEW_OPTIONS = [
  { id: "stores", displayText: "Stores" },
  { id: "store-groups", displayText: "Store Groups" },
];

export const STATUS = { ACTIVE: "ACTIVE", INACTIVE: "INACTIVE" };

// A record without a status is treated as ACTIVE.
export const isInactive = (status) => status === STATUS.INACTIVE;

export const createStatusTag = (status) => {
  const inactive = isInactive(status);
  return (
    <Tag
      text={inactive ? "Inactive" : "Active"}
      color={inactive ? TAG_COLORS.INACTIVE : TAG_COLORS.ACTIVE}
      isCopyable={false}
    />
  );
};

export const renderStoreImage = (imageUrl, name) =>
  imageUrl ? <img src={imageUrl} alt={name} style={STYLES.thumbnail} /> : "-";

export const renderCoordinate = (value) =>
  value == null || value === "" ? "-" : value;

export const createStoreActionButtons = (
  store,
  onEdit,
  onDelete,
  onView,
  isSyncMarket = false
) => {
  const deleteDisabled = store.deleted || isSyncMarket;
  return (
    <div style={STYLES.actions}>
      {onView && (
        <Icon
          icon="visibility"
          onClick={() => onView(store)}
          style={STYLES.viewIcon}
          title={MESSAGES.VIEW_STORE_TOOLTIP}
        />
      )}
      <Icon
        icon="edit"
        onClick={() => onEdit(store)}
        style={STYLES.editIcon}
        title={MESSAGES.EDIT_TOOLTIP}
      />
      <Icon
        icon="delete"
        onClick={deleteDisabled ? undefined : () => onDelete(store.id, store.name)}
        style={deleteDisabled ? STYLES.disabledIcon : STYLES.deleteIcon}
        title={
          deleteDisabled ? MESSAGES.DELETE_DISABLED_TOOLTIP : MESSAGES.DELETE_TOOLTIP
        }
      />
    </div>
  );
};

export const createGroupActionButtons = (
  groupName,
  onViewGroup,
  onEditGroup,
  onDeleteGroup
) => (
  <div style={STYLES.actions}>
    <Icon
      icon="visibility"
      onClick={() => onViewGroup(groupName)}
      style={STYLES.viewIcon}
      title={MESSAGES.VIEW_TOOLTIP}
    />
    <Icon
      icon="edit"
      onClick={() => onEditGroup(groupName)}
      style={STYLES.editIcon}
      title="Rename group"
    />
    <Icon
      icon="delete"
      onClick={() => onDeleteGroup(groupName)}
      style={STYLES.deleteIcon}
      title="Delete group"
    />
  </div>
);
